import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import AppIcon from "../assets/sayq-fav.svg";
import loginHero from "../assets/login_hero.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";

const API = "https://api18081113.queroapp.pl/demo";

export default function DemoGuest() {
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get('session');

    const [step, setStep] = useState(1); // 1 = Register, 2 = Chat
    const [nickname, setNickname] = useState("");

    // Chat states
    const [text, setText] = useState("");
    const [status, setStatus] = useState("idle"); // idle, sending, success, error
    const textareaRef = useRef(null);

    useEffect(() => {
        if (!sessionId) {
            setStatus("error");
        }
    }, [sessionId]);

    const handleRegister = (e) => {
        e.preventDefault();
        if (nickname.trim()) {
            setStep(2);
        }
    };

    const handleSkip = () => {
        setNickname("Gość");
        setStep(2);
    };

    const handleSend = async (e) => {
        e.preventDefault();
        if (!text.trim()) return;

        setStatus("sending");
        try {
            const res = await fetch(`${API}/send`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    session_id: sessionId,
                    text: text,
                    nick: nickname || "Gość"
                })
            });
            const data = await res.json();

            if (res.ok && data.success) {
                setStatus("success");
                setText("");
                setTimeout(() => setStatus("idle"), 2500);
            } else {
                setStatus("error");
            }
        } catch (err) {
            setStatus("error");
            console.error(err);
        }
    };

    if (!sessionId) {
        return (
            <div className="min-vh-100 d-flex flex-column justify-content-center align-items-center bg-light p-4 text-center">
                <h1 className="display-1 text-muted"><i className="bi bi-x-circle"></i></h1>
                <h2 className="mb-3">Błąd sesji</h2>
                <p>Nieprawidłowy link lub sesja wygasła. Zeskanuj kod QR ponownie z ekranu głównego.</p>
            </div>
        );
    }

    if (step === 1) {
        // --- REGISTRATION VIEW (Matches GuestRegister.jsx) ---
        return (
            <div className="d-flex w-100 overflow-hidden bg-white font-sans page-root-fixed">
                {/* LEFT COLUMN: FORM */}
                <div className="col-12 col-md-6 d-flex flex-column justify-content-center align-items-center p-4 p-md-5 relative overflow-y-auto w-100">
                    <div className="w-100" style={{ maxWidth: "420px" }}>
                        <div className="mb-4 text-center text-md-start">
                            <img src={AppIcon} alt="SayQ" className="rounded-3" style={{ width: 64, height: 64, objectFit: 'contain' }} />
                        </div>

                        <h1 className="fw-bold mb-2 text-dark text-center text-md-start" style={{ fontSize: '2rem' }}>Witaj!</h1>
                        <p className="text-secondary mb-5 text-center text-md-start">
                            Dołączasz do demonstracji na żywo <span className="fw-bold text-dark">SayQ App</span>.
                        </p>

                        <form onSubmit={handleRegister}>
                            <div className="mb-4">
                                <label htmlFor="guestName" className="form-label text-uppercase small fw-bold text-secondary ls-1" style={{ fontSize: '0.75rem' }}>
                                    Twoje Imię / Nick
                                </label>
                                <input
                                    id="guestName"
                                    type="text"
                                    className="form-control form-control-lg bg-light border-0 rounded-3 py-3 px-3 transition-all focus-ring"
                                    placeholder="np. Tomek"
                                    value={nickname}
                                    onChange={e => setNickname(e.target.value)}
                                    autoFocus
                                />
                            </div>

                            <button
                                type="submit"
                                className="btn btn-primary w-100 btn-lg rounded-pill fw-bold py-3 mb-3 shadow-primary hover-lift transition-all"
                                disabled={!nickname.trim()}
                                style={{ fontSize: '1rem', letterSpacing: '0.5px' }}
                            >
                                DOŁĄCZ DO WYDARZENIA
                            </button>

                            <button
                                type="button"
                                onClick={handleSkip}
                                className="btn btn-link w-100 text-secondary text-decoration-none small"
                            >
                                Pomiń i dołącz anonimowo
                            </button>
                        </form>
                    </div>
                </div>

                {/* RIGHT COLUMN: HERO IMAGE (Optional on mobile) */}
                <div className="d-none d-md-block col-md-6 p-0 position-relative bg-dark overflow-hidden">
                    <img src={loginHero} alt="Event Mood" className="w-100 h-100 object-fit-cover opacity-75" style={{ objectPosition: 'center' }} />
                    <div className="position-absolute top-0 start-0 w-100 h-100" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)' }}></div>
                    <div className="position-absolute bottom-0 start-0 w-100 p-5 mb-5 text-white">
                        <h2 className="display-4 fw-bold mb-3">Wersja Demo</h2>
                        <p className="lead opacity-75" style={{ maxWidth: 400 }}>Przetestuj szybkość wiadomości w czasie rzeczywistym.</p>
                    </div>
                </div>

                <style>{`
                    body { overflow-y: auto !important; overflow-x: hidden !important; }
                    .page-root-fixed { height: 100dvh; }
                    .ls-1 { letter-spacing: 1px; }
                    .hover-lift:hover { transform: translateY(-2px); box-shadow: 0 10px 25px -5px rgba(13,110,253, 0.4); }
                    .shadow-primary { box-shadow: 0 4px 14px 0 rgba(13,110,253, 0.39); }
                    .focus-ring:focus { box-shadow: 0 0 0 4px rgba(13,110,253, 0.1); background-color: #fff; }
                `}</style>
            </div>
        );
    }

    // --- CHAT VIEW (Matches Chat.jsx) ---
    return (
        <div id="page-root" className="page-root" style={{ height: '100dvh', display: 'flex', flexDirection: 'column', background: '#F3F4F6' }}>
            {/* TOPBAR */}
            <div className="bg-white shadow-sm border-bottom py-3 px-4 d-flex align-items-center justify-content-between" style={{ zIndex: 1000 }}>
                <div className="d-flex align-items-center gap-3">
                    <img src={AppIcon} alt="Logo" className="rounded-3" style={{ width: 36, height: 36 }} />
                    <h4 className="m-0 fs-6 fw-bold text-dark">SayQ Demo</h4>
                </div>
                <div className="text-secondary small fw-bold bg-light px-3 py-1 rounded-pill">
                    {nickname}
                </div>
            </div>

            {/* CONTENT */}
            <div className="container-chat d-flex justify-content-center align-items-center flex-grow-1 px-3 pb-5 pt-4 overflow-y-auto w-100">
                <div className="bg-white p-4 p-md-5 w-100 position-relative"
                    style={{
                        maxWidth: '550px',
                        borderRadius: '24px',
                        boxShadow: '0 20px 60px -10px rgba(0,0,0,0.08)'
                    }}>

                    <div className="text-center mb-5">
                        <h2 className="fw-black mb-2 text-break text-dark display-6" style={{ fontWeight: 800 }}>
                            Zadaj Pytanie
                        </h2>
                        <p className="text-secondary fw-medium mb-3">
                            <span className="fs-5 text-primary fw-bold text-break">Widok Gościa</span>
                        </p>
                    </div>

                    <form onSubmit={handleSend} className="d-flex flex-column align-items-center w-100">
                        <div className="w-100 mb-4">
                            <textarea
                                id="guest-question-area"
                                ref={textareaRef}
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                rows="4"
                                placeholder="Wpisz testową wiadomość..."
                                className="form-control minimal-input resize-none w-100"
                                style={{
                                    backgroundColor: '#fff',
                                    border: '2px solid #dee2e6',
                                    borderRadius: '12px',
                                    padding: '16px',
                                    boxShadow: 'none',
                                    outline: 'none',
                                    resize: 'none',
                                    fontSize: '1.1rem'
                                }}
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={!text.trim() || status === 'sending'}
                            className={`btn w-100 py-3 rounded-pill fw-bold shadow-sm text-uppercase ls-1 transition-all ${status === 'success' ? 'btn-success text-white' : 'btn-primary'}`}
                            style={{ letterSpacing: '0.5px' }}
                        >
                            {status === 'sending' && "Wysyłanie..."}
                            {status === 'success' && <span><FontAwesomeIcon icon={faCheckCircle} className="me-2" />Wysłano</span>}
                            {status === 'idle' && "Wyślij pytanie"}
                            {status === 'error' && "Błąd"}
                        </button>
                    </form>
                </div>
            </div>

            <footer className="w-100 text-center py-3 text-muted mt-auto" style={{ fontSize: '0.75rem', opacity: 0.6 }}>
                <span>SayQ App - Tryb Demonstracyjny</span>
            </footer>
        </div>
    );
}

